import React, {
  useState,
  useRef,
  useEffect,
  useCallback,
  useMemo,
} from "react";
import {
  Form,
  Input,
  message,
  Select,
  DatePicker,
  Row,
  Col,
  Popconfirm,
  Drawer,
  Button,
  Spin,
  Table,
} from "antd";
import {
  BankOutlined,
  LoadingOutlined,
  DeleteOutlined,
  CloudUploadOutlined,
  PlusOutlined,
  EyeOutlined
} from "@ant-design/icons";
import { useLocation } from "react-router-dom";
import http from "@/utils/http";
import api from "@/utils/api";
import dayjs from "dayjs";
import commonFn from "@/utils/commonFn";
import CustomUploadNewBank from "@/hocComponents/customUploadNewBank";
import InsuranceVPH from "./InsuranceVPH"; 
const mergePropertyOptions = (list = []) => {
  const map = new Map();
  list.forEach((item) => {
    if (
      !item ||
      item?.value === undefined ||
      item?.value === null ||
      item?.value === ""
    )
      return;
    const key = String(item.value);
    if (!map.has(key)) map.set(key, item);
  });
  return Array.from(map.values());
};

const InsuranceEditDetailPage = () => {
  const location = useLocation();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [statementFiles, setStatementFiles] = useState([]);
  const [propOptions, setPropOptions] = useState([]);
  const [propLoading, setPropLoading] = useState(false);
  const [anetList, setAnetList] = useState([]);
  const [agentLoading, setAgentLoading] = useState(false);
  const [buildingLoadingMap, setBuildingLoadingMap] = useState({});
  const [editId, setEditId] = useState(null);
  const [glAcctOptionsMap, setGlAcctOptionsMap] = useState({});
  const [filePaths, setFilePaths] = useState([]);
  const [btnDisabled, setBtnDisabled] = useState(false);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploadLoading, setUploadLoading] = useState(false);
  const insuranceVPHRef = useRef(null);
  const idCounterRef = useRef(0);
  const [buildingData, setBuildingData] = useState([]);

  const existingRows = useMemo(
    () => buildingData.filter((item) => !item._isNew),
    [buildingData],
  );
  const newRows = useMemo(
    () => buildingData.filter((item) => item._isNew),
    [buildingData],
  );

  const propAllRef = useRef([]);
  const propLoadedRef = useRef(false);
  const searchTimer = useRef();
  const glCacheRef = useRef({});
  const propCacheRef = useRef({});

  const columnStyle = {
    border: "1px solid #f0f0f0",
    padding: 20,
    borderRadius: 8,
    background: "#fafafa",
  };

    const buildMap = (list = []) => {
    return list.reduce((acc, item) => {
      acc[item.value] = item.label;
      return acc;
    }, {});
  };

  const updateItem = useCallback((id, updates) => {
    setBuildingData((prev) =>
      prev.map((item) => (item._id === id ? { ...item, ...updates } : item)),
    );
  }, []);

  const removeItem = useCallback((id) => {
    setBuildingData((prev) => prev.filter((item) => item._id !== id));
  }, []);

  const addItem = useCallback(() => {
    const newId = ++idCounterRef.current;
    setBuildingData((prev) => [
      ...prev,
    
      {
        _id: newId,
        amount: "",
        propId: null,
        propName: "",
        glChartId: null,
        glAcctTitle: "",
        liabilityDeductible: "",
        liabilityLimit: "",
        trustName: "",
        prop: "",
        totalUnits: "",
        units: "",
        _isNew: true,
      },
    ]);
  }, []);

  const deleteAll = useCallback(() => {
    setBuildingData([]);
    idCounterRef.current = 0;
  }, []);

  const totals = useCallback(() => {
    let totalAmount = 0;
    const uniquePropIds = new Set();
    buildingData.forEach((item) => {
      const num = parseFloat(String(item?.amount || "0").replace(/,/g, ""));
      if (!isNaN(num)) totalAmount += num;
      if (item?.propId) uniquePropIds.add(item.propId);
    });
    return {
      amount: totalAmount.toFixed(2),
      propCount: uniquePropIds.size,
      buildingCount: buildingData.length,
    };
  }, [buildingData]);

  const currentTotals = totals();

  const columns = useMemo(
    () => [
      {
        title: () => (
          <span>
            <span style={{ color: "red" }}>*</span> Amount
          </span>
        ),
        dataIndex: "amount",
        width: 100,
        render: (text, record) => (
          <Input
            prefix="$"
            value={text || ""}
            onChange={(e) => {
              const formatted = commonFn.inputAmountFormat(
                e.target.value,
                true,
                true,
              );
              updateItem(record._id, { amount: formatted });
            }}
            style={{ width: "100%" }}
          />
        ),
      },
      {
        title: () => (
          <span>
            <span style={{ color: "red" }}>*</span> Property
          </span>
        ),
        dataIndex: "propId",
        width: 320,
        render: (text, record) => {
          const currentOptions = mergePropertyOptions([
            ...(propOptions || []),
            record.propId
              ? {
                  value: record.propId,
                  label: record.propName || String(record.propId),
                }
              : null,
          ]);
          return (
            <Select
              value={record.propId}
              options={currentOptions}
              loading={propLoading}
              showSearch
              filterOption={false}
              onSearch={onTypeSearch}
              onDropdownVisibleChange={handleDropdownOpen}
              allowClear
              placeholder="Select property"
              style={{ width: "100%" }}
              onClear={() => {
                resetPropOptions();
                updateItem(record._id, {
                  propId: null,
                  propName: "",
                  glChartId: null,
                  glAcctTitle: "",
                  trustName: "",
                  prop: "",
                  totalUnits: "",
                });
              }}
              onChange={(value, option) => {
                updateItem(record._id, {
                  propId: value,
                  propName: option?.label || "",
                  glChartId: null,
                  glAcctTitle: "",
                });
                if (value) {
                  getPropDetailsFun(value, record._id);
                  fetchGlByProp(value);
                }
              }}
            />
          );
        },
      },
      {
        title: "GL / Insure Type",
        dataIndex: "glChartId",
        width: 140,
        render: (text, record) => (
          <Select
            value={record.glChartId}
            placeholder="Select GL Account"
            options={glAcctOptionsMap[record.propId] || []}
            loading={buildingLoadingMap[record._id]}
            allowClear
            showSearch
            optionFilterProp="label"
            style={{ width: "100%" }}
            onChange={(value, option) => {
              updateItem(record._id, {
                glChartId: value,
                glAcctTitle: option?.label || "",
              });
            }}
          />
        ),
      },
      {
        title: "Deductible",
        dataIndex: "liabilityDeductible",
        width: 150,
        render: (text, record) => (
          <Input
            placeholder="Deductible"
            value={text || ""}
            onChange={(e) =>
              updateItem(record._id, { liabilityDeductible: e.target.value })
            }
            style={{ width: "100%" }}
          />
        ),
      },
      {
        title: "Limit",
        dataIndex: "liabilityLimit",
        width: 150,
        render: (text, record) => (
          <Input
            placeholder="Limit"
            value={text || ""}
            onChange={(e) =>
              updateItem(record._id, { liabilityLimit: e.target.value })
            }
            style={{ width: "100%" }}
          />
        ),
      },
      {
        title: "Entity",
        dataIndex: "trustName",
        width: 140,
        render: (text, record) => (
          <Input
            readOnly
            value={text || ""}
            suffix={
              buildingLoadingMap[record._id] ? <LoadingOutlined spin /> : null
            }
            placeholder="Auto"
            style={{ width: "100%" }}
          />
        ),
      },
      {
        title: "Delete",
        width: 30,
        render: (_, record) =>
          buildingData.length > 1 && (
            <Button
              type="text"
              danger
              size="small"
              icon={<DeleteOutlined />}
              onClick={() => removeItem(record._id)}
            />
          ),
      },
      {
      title: "View",
       width: 30,
       render: (_, record) =>
       <Button
        type="text"  
        size="small"
        style={{ color: '#1890ff' }} 
        icon={<EyeOutlined />} 
        onClick={() => {
          console.log( record);
        insuranceVPHRef.current?.setModalOpen({ record });
        }}
      /> 
}
    ],
    [
      propOptions,
      propLoading,
      glAcctOptionsMap,
      buildingLoadingMap,
      buildingData.length,
      updateItem,
      removeItem,
    ],
  );

  const parseAmount = (value) => {
    if (value === undefined || value === null || value === "") return 0;
    const num = Number(String(value).replace(/,/g, "").trim());
    return Number.isFinite(num) ? num : 0;
  };

  const syncFileList = (files = []) => {
  setFilePaths(files);
};
  const getRecordId = () => new URLSearchParams(location.search).get("id");

  const getAgentTypeList = async () => {
    if (anetList.length > 0) return anetList;
    if (agentLoading) return [];
    setAgentLoading(true);
    try {
      const res = await http.get(api.getAgentType);
      const data = res?.data || res;
      const list = (data || []).map((item) => ({
        value: String(item.vendorId),
        label: item.vendorName,
      }));
      setAnetList(list);
      return list;
    } finally {
      setAgentLoading(false);
    }
  };
const loadGlOptionsForProps = async (propIds) => {
  const uniquePropIds = [...new Set(propIds.filter(Boolean))];
  
  if (uniquePropIds.length === 0) return {};
  
  const glMap = {};
  
  // 并发请求所有未缓存的 propId
  const requests = uniquePropIds.map(async (propId) => {
   
    if (glCacheRef.current[propId]) {
      glMap[propId] = glCacheRef.current[propId];
      return;
    }
    
    try {
      const res = await http.get(api.getInstanceByGlAcct, {
        params: { propId },
      });
      const options = (res?.data || []).map((opt) => ({
        label: opt.glAcctTitle,
        value: opt.glChartId,
      }));
      glCacheRef.current[propId] = options;
      glMap[propId] = options;
    } catch (error) {
      console.error(`Failed to fetch GL for propId ${propId}:`, error);
      glCacheRef.current[propId] = [];
      glMap[propId] = [];
    }
  });
  
  await Promise.all(requests);
  return glMap;
};

const MAX_GL_PROPS_THRESHOLD = 50; 
  const loadData = async (id) => {
    if (!id) return;
    setLoading(true);
    try {

      const agentList = await getAgentTypeList();
      const res = await http.get(api.getInsuranceById, { params: { id } });
      const data = res?.data;
      if (!data) return;

      idCounterRef.current = (data.buildingList || []).length;
      const list = (data.buildingList || []).map((item, index) => ({
        _id: index + 1,
        amount: item.amount
          ? commonFn.inputAmountFormat(String(item.amount), false, true)
          : "",
        propId: item.propId || null,
        propName: item.propName || "",
        glChartId: item.glChartId || null,
        glAcctTitle: item.glAcctTitle || "",
        liabilityDeductible: item.liabilityDeductible || "",
        liabilityLimit: item.liabilityLimit || "",
        trustName: item.trustName || item.owner || "",
        prop: item.prop || "",
        totalUnits: item.totalUnits || "",
        units: item.units || "",
        _isNew: false,
      //   _originalId: item.id,  
      // _originalPushBatch: item.pushBatch, 
      }));

      setBuildingData(list);
  const uniquePropIds = [...new Set(list.map(item => item.propId).filter(Boolean))];
    
    let glMap = {};
      if (uniquePropIds.length > MAX_GL_PROPS_THRESHOLD) {
  
      list.forEach((item) => {
        if (!item.propId) return;
        if (!glMap[item.propId]) {
          glMap[item.propId] = [];
        }
        if (item.glChartId && item.glAcctTitle) {
          const hasCurrentOption = glMap[item.propId].some(opt => opt.value === item.glChartId);
          if (!hasCurrentOption) {
            glMap[item.propId].push({
              value: item.glChartId,
              label: item.glAcctTitle,
            });
          }
        }
      });
    } else {
      // propId 数量不超过阈值，正常请求 GL 数据
      const propIds = list.map(item => item.propId);
      glMap = await loadGlOptionsForProps(propIds);
      
      // 合并接口返回的已有 GL 选项
      list.forEach((item) => {
        if (!item.propId) return;
        const existingOptions = glMap[item.propId] || [];
        
        if (item.glChartId && item.glAcctTitle) {
          const hasCurrentOption = existingOptions.some(opt => opt.value === item.glChartId);
          if (!hasCurrentOption) {
            existingOptions.unshift({
              value: item.glChartId,
              label: item.glAcctTitle,
            });
          }
        }
        glMap[item.propId] = existingOptions;
      });
    }
      // const glMap = {};
      // list.forEach((item) => {
      //   if (!item.propId || !item.glChartId) return;
      //   if (!glMap[item.propId]) glMap[item.propId] = [];
      //   if (!glMap[item.propId].find((g) => g.value === item.glChartId)) {
      //     glMap[item.propId].push({
      //       value: item.glChartId,
      //       label: item.glAcctTitle || item.glChartId,
      //     });
      //   }
      // });
      setGlAcctOptionsMap(glMap);

      const props = list
        .filter((item) => item.propId)
        .map((item) => ({
          value: item.propId,
          label: item.propName || String(item.propId),
        }));
      setPropOptions((prev) => mergePropertyOptions([...prev, ...props]));

        const agentMap = buildMap(agentList);
      form.setFieldsValue({
        expirationDate: data.expirationDate ? dayjs(data.expirationDate) : null,
        effectiveDate: data.effectiveDate ? dayjs(data.effectiveDate) : null,
        policyNumber: data.policyNumber,
        carrier: data.carrier,
        agent: data.agentVendorId ? String(data.agentVendorId) : undefined,
        payee: data.payeeVendorId ? String(data.payeeVendorId) : undefined,
        agentName: agentMap[String(data.agentVendorId)] || "",
        payeeName: agentMap[String(data.payeeVendorId)] || "",
      });

//  if (data.insurancePaymentFileList?.length) {
//   const fileList = data.insurancePaymentFileList.map((f) => ({
//     uid: String(f.uuid || f.fileUploadId || f.id),
//     uuid: String(f.uuid || f.fileUploadId || f.id),
//     fileUploadId: f.fileUploadId,
//     name: f.name || f.fileName,
//     fileName: f.name || f.fileName,
//     status: "done",
//     type: f.type || "new_insurance",
//     url: f.base64 || f.url,
//     path: f.path,
//     allPath: f.base64 || f.url,
//     ext: f.ext || f.suffix,
//     foreignId: f.foreignId,
//   }));
  
//   setStatementFiles(fileList);
//   setFilePaths(fileList);  
//   form.setFieldValue("fileList", fileList);
// }

// 统一处理所有文件
const allFiles = [];

// 处理支付文件
if (data.insurancePaymentFileList?.length) {
  const paymentFiles = data.insurancePaymentFileList.map((f) => ({
    uid: String(f.uuid || f.fileUploadId || f.id),
    uuid: String(f.uuid || f.fileUploadId || f.id),
    fileUploadId: f.fileUploadId,
    name: f.name || f.fileName,
    fileName: f.name || f.fileName,
    status: "done",
    type: f.type || "new_insurance",
    url: f.base64 || f.url,
    path: f.path,
    allPath: f.base64 || f.url,
    ext: f.ext || f.suffix,
    foreignId: f.foreignId,
  }));
  allFiles.push(...paymentFiles);
}

// 处理保单文件
if (data.insuranceFileList?.length) {
  const policyFiles = data.insuranceFileList.map((f) => ({
    uid: String(f.uuid || f.fileUploadId || f.id),
    uuid: String(f.uuid || f.fileUploadId || f.id),
    fileUploadId: f.fileUploadId,
    name: f.fileName || f.name,
    fileName: f.fileName || f.name,
    status: "done",
    type: f.type , 
    url: f.url,  
    path: f.path,
    allPath: f.url, 
    ext: f.ext || f.suffix,
    foreignId: f.foreignId,
  }));
  allFiles.push(...policyFiles);
}

// 一次性设置所有文件
if (allFiles.length > 0) {
  setStatementFiles(allFiles);
  setFilePaths(allFiles);
  form.setFieldValue("fileList", allFiles);
} else {
  setStatementFiles([]);
  setFilePaths([]);
  form.setFieldValue("fileList", []);
}
    } catch (err) {
      message.error("Failed to load details");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const id = getRecordId();
    if (id) {
      setEditId(id);
      loadData(id);
    }
  }, [location.search]);


  const propIdFun = async (content = "") => {
    setPropLoading(true);
    try {
      const res = await http.get(api.propSearch, { params: { content } });
      const formatted = (res?.data || []).map((item) => ({
        value: item.propId,
        label: item.propName,
        prop: item.prop,
      }));
      if (!content) {
        propAllRef.current = formatted;
        setPropOptions((prev) => mergePropertyOptions([...prev, ...formatted]));
      } else {
        setPropOptions(formatted);
      }
    } finally {
      setPropLoading(false);
    }
  };

  const handleDropdownOpen = (open) => {
    if (!open) return;
    setPropOptions((prev) => (prev.length ? prev : propAllRef.current));
    if (propLoadedRef.current) return;
    propIdFun();
    propLoadedRef.current = true;
  };

  const onTypeSearch = (value) => {
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => propIdFun(value), 300);
  };

  const resetPropOptions = () => setPropOptions(propAllRef.current);

  const getPropDetailsFun = async (propIdValue, rowId) => {
    if (!propIdValue) return;
    setBuildingLoadingMap((prev) => ({ ...prev, [rowId]: true }));
    try {
      const res = await http.get(api.getPropDetails, {
        params: { propId: propIdValue },
      });
      const detail = res?.data;
      if (detail) {
        updateItem(rowId, {
          units: detail.units || "",
          totalUnits: detail.units || "",
          trustName: detail.trustName || detail.owner || "",
          propName: detail.propName || "",
          prop: detail.prop || "",
          liabilityDeductible: detail.liabilityDeductible || "",
          liabilityLimit: detail.liabilityLimit || "",
        });
      }
    } catch {
      message.error("Failed to fetch property details");
    } finally {
      setBuildingLoadingMap((prev) => ({ ...prev, [rowId]: false }));
    }
  };

  const fetchGlByProp = async (propIdValue) => {
    if (!propIdValue) return;
    if (glCacheRef.current[propIdValue]) {
      setGlAcctOptionsMap((prev) => ({
        ...prev,
        [propIdValue]: glCacheRef.current[propIdValue],
      }));
      return;
    }
    const res = await http.get(api.getInstanceByGlAcct, {
      params: { propId: propIdValue },
    });
    const options = (res?.data || []).map((item) => ({
      label: item.glAcctTitle,
      value: item.glChartId,
    }));
    glCacheRef.current[propIdValue] = options;
    setGlAcctOptionsMap((prev) => ({ ...prev, [propIdValue]: options }));
  };

  const uploadByBankApi = async (file, fieldType) => {
    const fd = new FormData();
    fd.append("files", file.originFileObj);
    fd.append("type", fieldType);
    const res = await http.post(api.getInstanceByUpload, fd, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    if (res.code === "1") return res.data[0];
    throw new Error(res.msg || "Upload failed");
  };

const addOldFn = async (files) => {
  setBtnDisabled(true);
  setUploadLoading(true);
  try {
    const file = files?.[files.length - 1];
    if (!file?.originFileObj) return { flag: false };
    const uploaded = await uploadByBankApi(file, "new_insurance");
    const normalized = {
      uuid: uploaded?.uuid || file.uid,
      id: uploaded?.id,
      name: uploaded?.fileName || file.name,
      fileName: uploaded?.fileName || file.name,
      type: uploaded?.type,
      url: uploaded?.url,
      path: uploaded?.path,
      allPath: uploaded?.url || URL.createObjectURL(file.originFileObj),
      originFileObj: file.originFileObj,
      ext: uploaded?.ext,
    };
    
   
    const currentList = form.getFieldValue("fileList") || [];
    const newList = [...currentList, normalized];
    form.setFieldValue("fileList", newList);
    
    return { flag: true, fileData: normalized };
  } catch (error) {
    message.error(error?.message || "Upload failed");
    return { flag: false };
  } finally {
    setBtnDisabled(false);
    setUploadLoading(false);
  }
};

  const deleteOldFn = async () => true;
const deleteBase64FileFn = async (file) => {
  const fileUid = file?.uuid || file?.uid;
  
  const currentList = form.getFieldValue("fileList") || [];
  const newList = currentList.filter((item) => {
    const itemUid = item?.uuid || item?.uid;
    return itemUid !== fileUid;
  });
  
  form.setFieldValue("fileList", newList);
  
  return newList;
};


const onFinish = async () => {
  setLoading(true);
  try {
    
    for (let i = 0; i < buildingData.length; i++) {
      const item = buildingData[i];
      if (
        !item?.amount ||
        parseFloat(String(item.amount).replace(/,/g, "")) === 0
      ) {
        message.error(`Row ${i + 1}: Amount is required`);
        setLoading(false);
        return;
      }
      if (!item?.propId) {
        message.error(`Row ${i + 1}: Property is required`);
        setLoading(false);
        return;
      }
    }
    const values = form.getFieldsValue();
    const formatted = buildingData.map((item) => {
      const buildingItem = {
        amount: parseAmount(item.amount),
        policyUnits: Number(item.policyUnits) || 0,
        addressCityStateZip: item.addressCityStateZip || "",
        prop: item.prop || propCacheRef.current[item.propId]?.prop || "",
        propId: item.propId,
        totalUnits: Number(item.totalUnits || item.units) || 0,
        glChartId: item.glChartId,
        liabilityDeductible: item.liabilityDeductible,
        liabilityLimit: item.liabilityLimit,
      };
      
      // if (item._originalId) {
      //   buildingItem.id = item._originalId;
      // }
      // if (item._originalPushBatch) {
      //   buildingItem.pushBatch = item._originalPushBatch;
      // }
      return buildingItem;
    });

    const fileList = form.getFieldValue("fileList") || [];
    const paymentFile = fileList.map((item) => ({
      ext: item.ext,
      path: item.path,
      name: item.name,
      uuid: item.uuid || item.id || item.uid,
      type: item.type,
    }));

    const params = {
      amount: parseAmount(currentTotals.amount),
      insuranceId: editId,
      numberOfProperties: currentTotals.propCount,
      numberOfBuildings: currentTotals.buildingCount,
       expirationDate: values.expirationDate?.format("YYYY-MM-DD"),
        effectiveDate: values.effectiveDate?.format("YYYY-MM-DD"),
       agentVendorId: values.agent ? Number(values.agent) : undefined,
        payeeVendorId: values.payee ? Number(values.payee) : undefined,
       policyNumber: values.policyNumber,
        carrier: values.carrier,
       buildingList: formatted,
      paymentFile,
    };
    // console.log(values);
    // console.log(params);
    const res = await http.post(api.getInstanceEnter, params);
    if (res?.code === "1") {
      message.success(res?.msg ||"Insurance saved successfully.");
      setTimeout(() => {
        try {
          if (window.opener && !window.opener.closed) {
            window.opener.postMessage(
              { type: "INSURANCE_REFRESH", id: editId },
              "*",
            );
          }
          window.close();
          setTimeout(() => {
            if (!window.closed) window.location.href = "about:blank";
          }, 300);
        } catch {
          window.location.href = "about:blank";
        }
      }, 800);
    } else {
      message.error(res?.msg || "Save failed");
    }
  } catch (err) {
    console.error(err);
    if (!err?.errorFields) message.error("Request failed");
  } finally {
    setLoading(false);
  }
};

  const panelStyle = {
    ...columnStyle,
    height: "calc(100vh - 40px)",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
  };

  return (
    <div>
      <Spin spinning={loading} tip="Loading...">
        <div style={{ background: "#fff", borderRadius: 8 }}>
          <Form
            form={form}
             onValuesChange={(changedValues, allValues) => {
              if (changedValues.fileList !== undefined) {
                setFilePaths(allValues.fileList || []);}}}
            layout="horizontal"
            labelCol={{ span: 10 }}
            wrapperCol={{ span: 14 }}
            onFinish={onFinish}
          >
            <Row gutter={24} style={{ margin: 0 }}>
              <Col span={4}>
                <div style={columnStyle} >
                  <div
                    style={{ fontSize: 16, fontWeight: 600, marginBottom: 16 }}
                  >
                    <BankOutlined /> Insurance Detail
                  </div>
                  <Form.Item
                    name="expirationDate"
                    label="Expiration Date"
                    rules={[{ required: true }]}
                  >
                    <DatePicker style={{ width: "100%" }} />
                  </Form.Item>
                  <Form.Item
                    name="effectiveDate"
                    label="Effective Date"
                    rules={[{ required: true }]}
                  >
                    <DatePicker style={{ width: "100%" }} />
                  </Form.Item>
                  <Form.Item name="policyNumber" label="Policy Number">
                    <Input  />
                  </Form.Item>
                  <Form.Item
                    name="carrier"
                    label="Carrier"
                    rules={[{ required: true }]}
                  >
                    <Input  />
                  </Form.Item>
    
                   <Form.Item name="agent" label="Agent" rules={[{ required: true }]}>
                    <Select
                      style={{ width: "100%" }}
                      placeholder="Please select Agent"
                      options={anetList}
                      loading={agentLoading}
                      showSearch
                      optionFilterProp="label"
                      onDropdownVisibleChange={(open) => open && getAgentTypeList()}
                    />
                  </Form.Item>
                  <Form.Item name="payee" label="Payee" rules={[{ required: true }]}>
                    <Select
                      style={{ width: "100%" }}
                      placeholder="Please select Payee"
                      options={anetList}
                      loading={agentLoading}
                      showSearch
                      optionFilterProp="label"
                      onDropdownVisibleChange={(open) => open && getAgentTypeList()}
                    />
                  </Form.Item>
                  <Form.Item label="Total Amount">
                    <Input
                      prefix="$"
                      value={commonFn.inputAmountFormat(
                        currentTotals.amount,
                        false,
                        true,
                      )}
                      disabled
                    />
                  </Form.Item>
                  <Form.Item label="# of Properties">
                    <Input value={currentTotals.propCount} disabled />
                  </Form.Item>
                  <Form.Item label="# of Buildings">
                    <Input value={currentTotals.buildingCount} disabled />
                  </Form.Item>
                </div>
              </Col>

              <Col span={20}>
                <div style={panelStyle}>
                  {/* 工具栏 */}
                  <Row
                    justify="space-between"
                    align="middle"
                    style={{ marginBottom: 16 }}
                  >
                    <Col>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 12,
                        }}
                      >
                        <div style={{ fontSize: 16, fontWeight: 600 }}>
                          Building List
                        </div>
                        <Button
                          type="primary"
                          loading={loading || uploadLoading}
                          htmlType="submit"
                        >
                          Send For Approval
                        </Button>
                      </div>
                    </Col>
                    <Col>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 12,
                        }}
                      >
                        <Popconfirm
                          title="Delete all buildings?"
                          description="This will clear all building rows."
                          okText="Delete"
                          cancelText="Cancel"
                          onConfirm={deleteAll}
                        >
                          <Button danger>Delete All</Button>
                        </Popconfirm>
                        <Button
                          type="primary"
                          onClick={() => setUploadOpen(true)}
                          icon={<CloudUploadOutlined />}
                        >
                          Upload Invoice
                        </Button>
                      </div>
                    </Col>
                  </Row>

                  <div style={{ flex: 1, overflow: "auto", minHeight: 0 }}>
                    <Table
                      rowClassName="table-row-gradient"
                      columns={columns}
                      dataSource={existingRows}
                      rowKey="_id"
                      className="no-border-table"
                      bordered={false}
                      showHeader={true}
                      sticky={{ offsetHeader: 0 }}
                   scroll={{ x: 'max-content' }}
                      pagination={{
                        defaultPageSize: 50,
                        pageSizeOptions: ["30", "50", "100", "200"],
                        showSizeChanger: true,
                        showTotal: (total) => `Total ${total} items`,
                        position: ["bottomRight"],
                      }}
                      locale={{ emptyText: "No existing buildings." }}
                    />

                    {newRows.length > 0 && (
                      <div style={{ marginTop: 16 }}>
                        <div
                          style={{
                            color: "#1677ff",
                            fontSize: 14,
                            fontWeight: 600,
                            marginBottom: 8,
                            paddingLeft: 8,
                          }}
                        >
                          New Building
                        </div>
                        <Table
                          rowClassName="table-row-gradient"
                          columns={columns}
                          dataSource={newRows}
                          className="no-border-table"
                          rowKey="_id"
                          bordered={false}
                          showHeader={false}
                          pagination={false}
                          locale={{ emptyText: "" }}
                          scroll={{ x: 'max-content' }}
                        />
                      </div>
                    )}

                    <Button
                      type="dashed"
                      block
                      style={{ marginTop: 12 }}
                      onClick={addItem}
                      icon={<PlusOutlined />}
                    >
                      Add Building
                    </Button>
                  </div>
                </div>
              </Col>
            </Row>
          </Form>
        </div>
      </Spin>

      {/* 上传 Drawer */}
      <Drawer
        title="Upload PDF / Image"
        placement="right"
        width="40%"
        open={uploadOpen}
        onClose={() => !uploadLoading && setUploadOpen(false)}
        maskClosable={!uploadLoading}
        closable={!uploadLoading}
        destroyOnClose={false}
        maskClosable={false}
      >
        <div style={{ margin: 20 }}>
          <div style={{ marginBottom: 16 }}>
            Uploaded files only support JPG, PNG or PDF.
          </div>
          <Form.Item name="fileList" noStyle>
            <CustomUploadNewBank
              customConfig={{
                uploadButtonStyle: { top: "20px", right: "20px" },
                pdfHeight: "80vh",
                fileFullName: "name",
                notMultiple: true,
                accept: ".jpg, .jpeg, .png, .pdf",
                status: "add", 
                deleteOldFn,
                deleteBase64FileFn,
                addOldFn,
              }}
              setFilePaths={syncFileList}
              filePaths={filePaths}
              setBtnDisabled={setBtnDisabled}
              btnDisabled={btnDisabled}
            />
          </Form.Item>
        </div>
      </Drawer>

       <InsuranceVPH ref={insuranceVPHRef} />
    </div>
  );
};

export default InsuranceEditDetailPage;
